<template>
  <div class="add-work-set-check-group-schema">
    <q-form @submit="onSubmit" class="form">
      <div class="form__item">
        <CustomSelect
          label="Тип проекта"
          :required="true"
          placeholder="Выберите тип проекта"
          :options="projectTypesOptions"
          option-value-key="project_type_id"
          option-label-key="project_type_name"
          :value="formData.project_type_id"
          @change="onProjectTypeChange"
        />
      </div>
      <div class="form__item">
        <CustomSelect
          label="Группа работ"
          :required="true"
          placeholder="Выберите группу работ"
          :options="workSetCheckGroupsOptions"
          option-value-key="work_set_check_group_id"
          option-label-key="work_set_check_group_name"
          :value="formData.work_set_check_group_id"
          :loading="isGroupsLoading"
          @change="(val) => (formData.work_set_check_group_id = val)"
        />
      </div>
      <div class="form__item">
        <CustomSelect
          label="Схема"
          :required="true"
          placeholder="Выберите схему"
          :options="schemaOptions"
          option-value-key="schema_id"
          option-label-key="schema_name"
          :value="formData.schema_id"
          @change="(val) => (formData.schema_id = val)"
        />
      </div>
      <CustomModalFooter
        :btn-loading="loading"
        :submit-btn-text="isEdit ? 'Сохранить' : 'Добавить'"
        :disable-submit="!canSubmit"
      />
    </q-form>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import CustomSelect from "@/components/form/CustomSelect";
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import {
  getWorkSetCheckGroups,
  getWorkSetCheckGroupsByProjectType,
} from "@/views/handbook/WorkSetCheckGroupSchema/services";

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  closeModal: {
    type: Function,
    default: () => {},
  },
});

const loading = ref(false);
const formData = reactive({
  project_type_id: null,
  work_set_check_group_id: null,
  schema_id: null,
});

const projectTypesOptions = computed(
  () => props.data?.projectTypesOptions ?? []
);
const schemaOptions = computed(() => props.data?.schemaOptions ?? []);

function normalizeOptionalId(val) {
  if (val === undefined || val === null || val === "") return null;
  return val;
}

const projectTypeIdForGroups = computed(() =>
  normalizeOptionalId(formData.project_type_id)
);

const { data: groupsAllData, isLoading: isGroupsAllLoading } = useQuery({
  queryKey: ["workSetCheckGroupsAddSchemaModal", "all"],
  queryFn: getWorkSetCheckGroups,
  enabled: computed(() => projectTypeIdForGroups.value == null),
  refetchOnWindowFocus: false,
});

const { data: groupsByTypeData, isLoading: isGroupsByTypeLoading } = useQuery({
  queryKey: computed(() => [
    "workSetCheckGroupsAddSchemaModal",
    "byType",
    projectTypeIdForGroups.value,
  ]),
  queryFn: () =>
    getWorkSetCheckGroupsByProjectType(projectTypeIdForGroups.value),
  enabled: computed(() => projectTypeIdForGroups.value != null),
  refetchOnWindowFocus: false,
});

const workSetCheckGroupsOptions = computed(() => {
  const raw =
    projectTypeIdForGroups.value != null
      ? groupsByTypeData.value
      : groupsAllData.value;
  return raw ?? [];
});

const isGroupsLoading = computed(() =>
  projectTypeIdForGroups.value != null
    ? isGroupsByTypeLoading.value
    : isGroupsAllLoading.value
);

const editingRow = computed(() => props.data?.editingRow ?? null);
const isEdit = computed(() => !!editingRow.value?.work_set_check_group_schema_id);

function resetFromEditing() {
  const row = editingRow.value;
  if (row) {
    formData.project_type_id = row.project_type_id ?? null;
    formData.work_set_check_group_id = row.work_set_check_group_id ?? null;
    formData.schema_id = row.schema_id ?? null;
  } else {
    formData.project_type_id = null;
    formData.work_set_check_group_id = null;
    formData.schema_id = null;
  }
}

watch(
  () => props.data?.editingRow,
  () => resetFromEditing(),
  { immediate: true }
);

function onProjectTypeChange(val) {
  formData.project_type_id = normalizeOptionalId(val);
  formData.work_set_check_group_id = null;
}

const canSubmit = computed(
  () =>
    formData.project_type_id != null &&
    formData.work_set_check_group_id != null &&
    formData.schema_id != null
);

async function onSubmit() {
  if (!canSubmit.value) return;
  loading.value = true;
  const body = {
    project_type_id: formData.project_type_id,
    work_set_check_group_id: formData.work_set_check_group_id,
    schema_id: formData.schema_id,
  };
  if (isEdit.value) {
    body.work_set_check_group_schema_id =
      editingRow.value.work_set_check_group_schema_id;
  }
  await props.data?.onSubmit(body);
  loading.value = false;
}
</script>

<style scoped lang="scss">
.add-work-set-check-group-schema {
  max-width: 520px;
  width: 300px;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 15px;

  &__item {
    width: 100%;
  }
}
</style>
